import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, exhaustMap, pipe, tap } from 'rxjs';

import { SettingsApi } from '../data/api/settings.api';
import type { GeoPoint } from '../data/models/address/address.model';

/** TODO (фаза 9, вопрос №3): заменяется координатами точки организации из `OrgInfo`. */
export const DEFAULT_MAP_CENTER: GeoPoint = Object.freeze({
  latitude: 41.311081,
  longitude: 69.240562,
});

/** Обзорный зум: виден город, а не двор — точки ещё нет, показывать нечего. */
export const DEFAULT_MAP_ZOOM = 12;

/** Зум, когда точка известна: дом различим, но не «внутри подъезда». */
export const POINT_MAP_ZOOM = 17;

/** Своя карта (OSM на map.pospoint.uz) работает без ключей; Яндекс — только с заведёнными ключами. */
export type MapProvider = 'pospoint' | 'yandex';

/** Ключ уходит в URL тайлов и в query геокодера: строка с `&`, `/` или пробелом подменила бы запрос. */
const API_KEY_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

/** Имена настроек группы `webDelivery`: список плоский, порядок и состав не гарантированы. */
const PROVIDER_SETTING = 'mapProvider';
const TILES_SETTING = 'yandexTilesApiKey';
const GEOCODER_SETTING = 'yandexGeocoderApiKey';
const SUGGEST_SETTING = 'yandexSuggestApiKey';

/** Пустая строка в настройке = ключ не заведён; сюда же уходят отсутствующий и битый. */
function sanitizeKey(value: unknown): string {
  return typeof value === 'string' && API_KEY_PATTERN.test(value) ? value : '';
}

/** Всё, кроме явного «yandex», — своя карта: ей не нужны ключи, поэтому это безопасный дефолт. */
function readProvider(value: unknown): MapProvider {
  return typeof value === 'string' && value.trim().toLowerCase() === 'yandex'
    ? 'yandex'
    : 'pospoint';
}

type MapConfigState = {
  provider: MapProvider;
  tilesKey: string;
  geocoderKey: string;
  suggestKey: string;
  /** Точка, выбранная последней в этой сессии, — начальный центр следующего открытия. */
  lastPoint: GeoPoint | null;
};

/** Провайдера и ключи выбирает владелец организации в админке; до ответа настроек работает своя карта. */
export const MapConfigStore = signalStore(
  { providedIn: 'root' },
  withState<MapConfigState>({
    provider: 'pospoint',
    tilesKey: '',
    geocoderKey: '',
    suggestKey: '',
    lastPoint: null,
  }),

  withComputed(({ provider, tilesKey, geocoderKey, suggestKey }) => {
    const own = computed(() => provider() === 'pospoint');
    return {
      /** Карта филиалов: своей карте ключ не нужен, Яндексу — ключ тайлов. */
      canShowMap: computed(() => own() || tilesKey().length > 0),
      /** У Яндекса нужны ОБА ключа: карта без геокодера вернёт только числа, а поле ждёт «Улица, номер дома». */
      canPickOnMap: computed(() => own() || (tilesKey().length > 0 && geocoderKey().length > 0)),
      /** GPS-кнопка с читаемым адресом — достаточно геокодера, карта ей не нужна. */
      canReverseGeocode: computed(() => own() || geocoderKey().length > 0),
      /** У Яндекса нужны ОБА: саджест отдаёт только текст и uri, координаты по uri добирает геокодер. */
      canSuggest: computed(() => own() || (suggestKey().length > 0 && geocoderKey().length > 0)),
    };
  }),

  withMethods((store, api = inject(SettingsApi)) => ({
    setLastPoint(point: GeoPoint): void {
      patchState(store, { lastPoint: point });
    },

    /** Вызывается один раз при старте приложения; exhaustMap гасит повторный вызов на лету. */
    load: rxMethod<void>(
      pipe(
        exhaustMap(() =>
          api.load().pipe(
            tap((settings) =>
              patchState(store, {
                provider: readProvider(settings.get(PROVIDER_SETTING)),
                tilesKey: sanitizeKey(settings.get(TILES_SETTING)),
                geocoderKey: sanitizeKey(settings.get(GEOCODER_SETTING)),
                suggestKey: sanitizeKey(settings.get(SUGGEST_SETTING)),
              }),
            ),
            // Отказ настроек — остаётся своя карта; catchError во ВНУТРЕННЕМ pipe, иначе повторный `load()` мёртв.
            catchError(() => EMPTY),
          ),
        ),
      ),
    ),
  })),
);
